import { useEffect } from 'react';
import { arrange, asc, tidy } from '@tidyjs/tidy';
import {
  Alert,
  Box,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';

import Flex from '@Components/Bases/Flex';
import { FormInput } from '@Components/Form';
import FormSelect from '@Components/Form/FormSelect/FormSelect';
import {
  disabledFormInputStyles,
  disabledFormSelectStyles,
} from '@Components/Form/styles';
import { useGetInfoCategoryMasterData } from '@Domains/MasterData';
import { INFO_CATEGORY_MASTER_DATA } from 'src/Enum/MasterData';
import { useGreenhouseForm } from '../../EsgGreenhouseFormProvider';
import { VerifierType, VerifyType } from 'src/Enum/EsgGreenhouse';
import GhGasEmissionVerifyLabel from '@Router/EsgGreenhouse/Components/GhGasEmissionVerifyLabel';

const GreenhouseGasEmissionVerifier = () => {
  const [{ data = [], loading, error }] = useGetInfoCategoryMasterData([
    INFO_CATEGORY_MASTER_DATA.GHG_VERIFIER,
  ]);

  const { setValue, watch, trigger } = useGreenhouseForm();
  const [ghGasEmissionVerify, completedSection] = watch([
    'performanceGhGasMgmtReport.ghGasEmissionVerify',
    'performanceGhGasMgmtReport.completedSection',
  ]);
  const { verify, verifier } = ghGasEmissionVerify || {};

  const selectedVerifierId = verifier?.infoCategoryMasterDataId;

  const verifierOptions = tidy(data, arrange([asc('sequence')]));

  const { isCompletedTh = false, isCompletedEn = false } =
    completedSection ?? {};

  const isDisabledFormSelect =
    !verify || verify === VerifyType.NO || isCompletedTh || isCompletedEn;

  useEffect(() => {
    if (selectedVerifierId !== VerifierType.GHG_VERIFIER_OTHER_ID) {
      setValue(
        'performanceGhGasMgmtReport.ghGasEmissionVerify.verifier.otherNameTh',
        ''
      );
      setValue(
        'performanceGhGasMgmtReport.ghGasEmissionVerify.verifier.otherNameEn',
        ''
      );
    }
  }, [verifier, selectedVerifierId]);

  if (loading) {
    return (
      <Box mt={2}>
        <Typography>Loading ...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={2}>
        <Alert severity={'error'}>
          ไม่สามารถโหลดข้อมูลรายชื่อหน่วยงานผู้ทวนสอบข้อมูลปริมาณการปล่อยก๊าซเรือนกระจกได้
        </Alert>
      </Box>
    );
  }

  const verifierData = data?.find((item) => item.id === selectedVerifierId);
  const isOtherVerifier =
    verifierData?.id === VerifierType.GHG_VERIFIER_OTHER_ID;

  const handleVerifierChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value;

    setValue(
      'performanceGhGasMgmtReport.ghGasEmissionVerify.verifier.infoCategoryMasterDataId',
      value as number
    );

    trigger(
      'performanceGhGasMgmtReport.ghGasEmissionVerify.verifier.infoCategoryMasterDataId'
    );
  };

  return (
    <Flex mt={2} gap={2}>
      <GhGasEmissionVerifyLabel required>
        รายชื่อหน่วยงานผู้ทวนสอบข้อมูลปริมาณการปล่อยก๊าซเรือนกระจก
        <Tooltip
          placement="top"
          arrow
          title={
            'รายชื่อหน่วยงานทวนสอบที่ระบุควรได้รับการยอมรับอย่างแพร่หลายในระดับสากล หรือ ได้รับการรับรองจากองค์การบริหารจัดการก๊าซเรือนกระจก (อบก.)'
          }
        >
          <HelpOutlineOutlinedIcon sx={{ fontSize: 16, mt: 0.5, ml: 0.5 }} />
        </Tooltip>
      </GhGasEmissionVerifyLabel>
      <Flex flexDirection="column" gap={1.5} width={'100%'}>
        <Flex gap={1.5}>
          <FormSelect
            name="performanceGhGasMgmtReport.ghGasEmissionVerify.verifier.infoCategoryMasterDataId"
            label="ภาษาไทย"
            placeholder="กรุณาเลือกรายชื่อหน่วยงานผู้ทวนสอบข้อมูลปริมาณการปล่อยก๊าซเรือนกระจก"
            sx={isDisabledFormSelect ? disabledFormSelectStyles : undefined}
            onChange={handleVerifierChange}
          >
            {verifierOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.nameTh}
              </MenuItem>
            ))}
          </FormSelect>
          <FormInput
            name="performanceGhGasMgmtReport.ghGasEmissionVerify.verifier.nameEn"
            label="ภาษาอังกฤษ"
            fullWidth
            value={
              verify && verify === VerifyType.YES ? verifierData?.nameEn : ''
            }
            sx={disabledFormInputStyles}
          />
        </Flex>
        <Flex gap={1.5}>
          <FormInput
            name="performanceGhGasMgmtReport.ghGasEmissionVerify.verifier.otherNameTh"
            placeholder={'กรุณาระบุชื่อหน่วยงานอื่น ๆ (ไทย)'}
            fullWidth
            value={verifier?.otherNameTh ?? ''}
            sx={
              !isOtherVerifier || isCompletedTh
                ? disabledFormInputStyles
                : undefined
            }
          />
          <FormInput
            name="performanceGhGasMgmtReport.ghGasEmissionVerify.verifier.otherNameEn"
            placeholder={'กรุณาระบุชื่อหน่วยงานอื่น ๆ (อังกฤษ)'}
            fullWidth
            value={verifier?.otherNameEn ?? ''}
            sx={
              !isOtherVerifier || isCompletedEn
                ? disabledFormInputStyles
                : undefined
            }
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

export default GreenhouseGasEmissionVerifier;
